import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Checkbox,
  Divider,
  Alert
} from '@mui/material';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Dayjs } from 'dayjs';

const GET_DAY_DAILIES = gql`
  query GetDayDailies($date: String!) {
    getDay(date: $date) {
      id
      completedDailies {
        id
      }
    }
    getDailies(activeOnly: true) {
      id
      title
    }
  }
`;

const TOGGLE_DAILY = gql`
  mutation ToggleDailyCompletion($date: String!, $dailyId: ID!) {
    toggleDailyCompletion(date: $date, dailyId: $dailyId) {
      id
      completedDailies {
        id
      }
    }
  }
`;

interface DailiesListProps {
  selectedDate: Dayjs;
}

const DailiesList: React.FC<DailiesListProps> = ({ selectedDate }) => {
  const dateStr = selectedDate.format('YYYY-MM-DD');
  const { data, loading, error } = useQuery(GET_DAY_DAILIES, {
    variables: { date: dateStr },
    fetchPolicy: 'cache-and-network'
  });

  const [toggleDaily] = useMutation(TOGGLE_DAILY, {
    refetchQueries: ['GetCalendarData', 'GetDayDailies']
  });

  if (loading && !data) return null;
  if (error) return <Alert severity="error">Failed to load dailies: {error.message}</Alert>;

  const activeDailies = data?.getDailies || [];
  const completedIds = new Set(data?.getDay?.completedDailies?.map((d: any) => d.id) || []);

  const handleToggle = async (dailyId: string) => {
    const dayId = data?.getDay?.id;
    const optimisticResponse = dayId ? {
      toggleDailyCompletion: {
        __typename: 'Day' as const,
        id: dayId,
        completedDailies: completedIds.has(dailyId) 
          ? data.getDay.completedDailies.filter((d: any) => d.id !== dailyId)
          : [...data.getDay.completedDailies, { __typename: 'Daily' as const, id: dailyId }]
      }
    } : undefined;

    await toggleDaily({
      variables: { date: dateStr, dailyId },
      optimisticResponse
    });
  };

  if (activeDailies.length === 0) return null;

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Daily Habits
      </Typography>
      <List sx={{ pt: 0 }}>
        {activeDailies.map((daily: any) => (
          <React.Fragment key={daily.id}>
            <ListItem 
              disablePadding
              sx={{ py: 0.5 }}
            >
              <Box 
                onClick={() => handleToggle(daily.id)}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: '100%' }}
              >
                <Checkbox 
                  checked={completedIds.has(daily.id)}
                  size="small"
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemText 
                  primary={daily.title}
                  primaryTypographyProps={{ 
                    sx: { 
                      fontSize: '0.9rem',
                      textDecoration: completedIds.has(daily.id) ? 'line-through' : 'none',
                      opacity: completedIds.has(daily.id) ? 0.6 : 1
                    } 
                  }}
                />
              </Box>
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default DailiesList;
